import React, { Suspense, useState, useEffect } from 'react'
import { HashRouter, Route, Routes } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { CSpinner, useColorModes } from '@coreui/react'
import axios from 'axios'
import jwtDecode from 'jwt-decode'

import './scss/style.scss'
import './styles/dark-theme.css'
import './styles/sidebar-patterns.css'

// Contexts
import { ThemeProvider } from './contexts/ThemeContext'

import { FocusProvider } from './contexts/FocusContext'

// Components
import DiscoMode from './components/DiscoMode'

// Containers
const DefaultLayout = React.lazy(() => import('./layout/DefaultLayout'))

// Pages
const Login = React.lazy(() => import('./views/login/Login'))
const Register = React.lazy(() => import('./views/pages/register/Register'))
const Page404 = React.lazy(() => import('./views/pages/page404/Page404'))
const Page500 = React.lazy(() => import('./views/pages/page500/Page500'))
const ForgotPassword = React.lazy(() => import('./views/login/ForgotPassword'))
const TaxChecklistFormStandalone = React.lazy(() => import('./views/public/TaxChecklistForm'))
const OnboardingForm = React.lazy(() => import('./views/public/OnboardingForm'))
const ProspectOnboardingForm = React.lazy(() => import('./views/public/ProspectOnboardingForm'))
const PrivacyPolicy = React.lazy(() => import('./views/public/PrivacyPolicy'))

// Axios interceptor for session inactivity
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const code = error.response?.data?.code
    if (code === 'SESSION_INACTIVE' || code === 'SESSION_REVOKED') {
      if (!window.isLoggingOut) {
        window.isLoggingOut = true
        const deviceId = localStorage.getItem('deviceId')
        localStorage.clear()
        if (deviceId) localStorage.setItem('deviceId', deviceId)
        window.location.href = code === 'SESSION_REVOKED' ? '#/login?reason=revoked' : '#/login?reason=inactivity'
      }
    }
    return Promise.reject(error)
  },
)

const App = () => {
  const { isColorModeSet, setColorMode } = useColorModes('coreui-free-react-admin-template-theme')
  const storedTheme = useSelector((state) => state.theme)
  const [darkMode, setDarkMode] = useState(false)

  useEffect(() => {
    // Load saved theme preference
    const savedTheme = localStorage.getItem('theme') || 'light'
    setDarkMode(savedTheme === 'dark')
    document.documentElement.setAttribute('data-theme', savedTheme)
    setColorMode(savedTheme === 'dark' ? 'dark' : 'light')

    // Check token expiration
    const token = localStorage.getItem('usertoken')

    if (token) {
      try {
        const decoded = jwtDecode(token)
        const currentTime = Date.now() / 1000 // Convert to seconds

        // Check if token is expired
        if (decoded.exp && decoded.exp < currentTime) {
          console.log('Token expired, clearing and redirecting to login')
          localStorage.removeItem('usertoken')

          if (!window.location.hash.includes('login')) {
            window.location.href = '#/login?reason=expired'
          }
        }
      } catch (error) {
        console.error('Invalid token format, clearing token:', error)
        localStorage.removeItem('usertoken')

        if (!window.location.hash.includes('login')) {
          window.location.href = '#/login'
        }
      }
    }

  }, [])

  return (
    <ThemeProvider>
      <FocusProvider>
        <HashRouter>
          <DiscoMode />
          <Suspense
            fallback={
              <div className="pt-3 text-center">
                <CSpinner color="primary" variant="grow" />
              </div>
            }
          >
            <Routes>
              {/* PUBLIC ROUTES - No authentication required */}
              <Route path="/tax-form/:token" element={<TaxChecklistFormStandalone />} />
              <Route path="/onboarding-form/:token" element={<OnboardingForm />} />
              <Route path="/onboarding-request/:token" element={<ProspectOnboardingForm />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />

              {/* AUTHENTICATION ROUTES */}
              <Route path="/login" name="Login Page" element={<Login />} />
              <Route path="/forgot-password" name="Forgot password Page" element={<ForgotPassword />} />
              <Route path="/register" name="Register Page" element={<Register />} />
              <Route path="/404" name="Page 404" element={<Page404 />} />
              <Route path="/500" name="Page 500" element={<Page500 />} />

              {/* PROTECTED ROUTES - Must be last to catch all other routes */}
              <Route path="*" name="Home" element={<DefaultLayout />} />
            </Routes>
          </Suspense>
        </HashRouter>
      </FocusProvider>
    </ThemeProvider>
  )
}

export default App
